import { PageFn } from '@client/Router';
import { isNullOrUndefined, SessionType } from '@shared';

import { useClientData } from '../hooks/useClientData';
import { FullScreenLoadingOverlay } from '@client/components';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchSubordinateAdvisorsByAdvisorIdRequest,
  selectAdvisorHierarchyState,
  selectClientErrorMessage,
  selectSession,
  UnauthorizedPage,
} from '..';
import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { LoginRedirect } from './LoginRedirect';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';

export const ClientRoute = ({
  component,
  allowedSessionTypes,
}: {
  component: PageFn;
  allowedSessionTypes: SessionType[];
}) => {
  const params = useParams();
  const { isClerkSignedIn } = useClerkClientAuth();
  const { client } = useClientData();
  const clientError = useSelector(selectClientErrorMessage);
  const Component = component;
  const session = useSelector(selectSession);
  const { subordinateAdvisors } = useSelector(selectAdvisorHierarchyState);
  const dispatch = useDispatch();

  useEffect(() => {
    if (
      session.sessionType === SessionType.advisor &&
      isNullOrUndefined(subordinateAdvisors) &&
      session.advisor?.advisorId
    ) {
      // fetch subordinate advisors if not already fetched. This is mainly for PDF report download
      // since it opens in a new window and fetch session does not fetch subordinate advisors
      dispatch(
        fetchSubordinateAdvisorsByAdvisorIdRequest({
          advisorId: session.advisor.advisorId,
        }),
      );
    }
  }, [session.sessionType, subordinateAdvisors, session.advisor?.advisorId]);

  if (!isClerkSignedIn) {
    return <LoginRedirect />;
  }

  if (clientError?.includes('Requested client not found.')) {
    return (
      <Navigate
        to="/client/home"
        replace
      />
    );
  }

  if (shouldShowLoading(client, session, subordinateAdvisors, params.clientId)) {
    return <FullScreenLoadingOverlay isLoading={true} />;
  }

  if (session.sessionType === SessionType.advisor) {
    const { advisor } = session;
    if (!advisorUserHasAccessToViewClient(advisor!, subordinateAdvisors || [], client!)) {
      return <UnauthorizedPage />;
    }
  }

  if (session.sessionType === SessionType.client) {
    if (!clientUserHasAccessToViewClient(client!.clientId, session.clientProfiles!)) {
      return <UnauthorizedPage />;
    }
  }

  return <Component {...params} />;
};

function shouldShowLoading(
  client: Client | null,
  session: Session,
  subordinateAdvisors: Advisor[] | null,
  clientIdFromRoute: string | undefined,
): boolean {
  if (!client) {
    return true;
  }

  if (clientIdFromRoute !== undefined && client.clientId !== clientIdFromRoute) {
    return true;
  }

  if (session.sessionType === SessionType.advisor) {
    return !session.advisor || isNullOrUndefined(subordinateAdvisors);
  }

  if (session.sessionType === SessionType.client) {
    return !session.clientProfiles;
  }

  return false;
}

function advisorUserHasAccessToViewClient(
  advisor: Advisor,
  subordinateAdvisors: Advisor[],
  client: Client,
): boolean {
  if (client.advisorId === advisor.advisorId) {
    return true;
  }
  const hasSubordinateAdvisors = subordinateAdvisors?.length > 0;
  return (
    hasSubordinateAdvisors &&
    subordinateAdvisors.some(subordinate => subordinate.advisorId === client.advisorId)
  );
}

function clientUserHasAccessToViewClient(
  clientId: string,
  clientProfiles: ClientProfile[],
): boolean {
  // if client user, check if clientId is within the clientProfiles
  return clientProfiles.some(profile => profile.clientId === clientId);
}
